import { useCallback, useEffect, useRef, useState } from "react";
import * as R from "ramda";

import { AnswerContextValue, useAnswerContext } from "contexts/answerContext";
import useGeocoder from "hooks/useGeocoder";
import { useFindFields } from "queries/entities";
import { useFieldByKey } from "queries/fields";
import { getAnswer, getUnansweredCountForFieldIdents } from "queries/projects";
import { useIsInBoundary } from "queries/tiles";
import { useZoningData } from "queries/zoning";
import { getProjectRequirementFieldIDs, getZoningFieldIDs } from "reducers/projects";
import { FieldSummary } from "types/configEntities";
import { AddressAnswer, Field } from "types/entities";
import { getFullAddress } from "utils/address";
import { isFalsy } from "utils/func";

import useWaitFor from "./useWaitFor";

interface UseZoningField {
  isInCityBoundary: boolean;
  conditionFields: FieldSummary[];
  unansweredFieldCount: number;
  onLocationChange: UseOnLocationChange;
  onAddressChange: any;
}

interface Coordinates {
  lat?: number;
  lng?: number;
}

const useZoningField = (addressField: Field, answerContext: AnswerContextValue): UseZoningField => {
  const { record, onChange } = answerContext;

  const { data: zoningField } = useFieldByKey("zoning");
  const zoningFieldIdents = getFilteredZoningFieldIdents(answerContext);
  const { data: _conditionFields = [] } = useFindFields(zoningFieldIdents, {
    select: R.reject(R.propEq(true, "calculated")) as (fields: any) => any,
  });
  const conditionFields = _conditionFields as FieldSummary[];

  const [coordinates, setCoordinates] = useState<Coordinates>();
  const [isInCityBoundary, setIsInCityBoundary] = useState(true);

  useEffect(() => {
    if (!coordinates && addressField) {
      const { latitude: lat, longitude: lng } = getAnswer(record, addressField.key) || {};
      setCoordinates({ lat, lng });
    }
  }, [coordinates, addressField, record]);

  const { data: zoningData } = useZoningData({
    record,
    lat: coordinates?.lat,
    lng: coordinates?.lng,
  });

  useEffect(() => {
    if (zoningData && zoningField) {
      onChange(zoningField, zoningData);
    }
  }, [zoningData, zoningField, onChange]);

  const onLocationChange = useOnLocationChange({
    addressField,
    setIsInCityBoundary,
    setCoordinates,
  });

  const onAddressChange = useCallback(
    async ({ value }) => {
      if (!value) return;

      await onLocationChange(value);
    },
    [onLocationChange],
  );

  const unansweredFieldCount = getUnansweredCountForFieldIdents(
    record,
    R.map(R.prop("identifier"), conditionFields),
  );

  return {
    conditionFields,
    isInCityBoundary,
    onAddressChange,
    onLocationChange,
    unansweredFieldCount,
  };
};

type UseOnLocationChange = (value: AddressAnswer) => Promise<AddressAnswer>;
const useOnLocationChange = ({
  addressField,
  setCoordinates,
  setIsInCityBoundary,
}): UseOnLocationChange => {
  const answerContext = useAnswerContext();
  const geocoder = useGeocoder(addressField);
  const { pending, onChange, onSave } = answerContext;
  const checkCoordsInBoundary = useIsInBoundary();
  const latestRequestRef = useRef(0);
  const blockWhileSaving = useWaitFor(pending, isFalsy);

  const handleLocationChange = useCallback(
    async (value: AddressAnswer) => {
      await blockWhileSaving();
      latestRequestRef.current += 1;
      const requestId = latestRequestRef.current;
      const { latitude, longitude } = value;

      let fullAddressValue = value;
      const isStale = () => requestId !== latestRequestRef.current;

      onChange(addressField, value);
      setCoordinates({ lat: latitude, lng: longitude });

      if (!getFullAddress(value)) {
        try {
          const address = await geocoder.reverseGeocode(latitude, longitude);
          fullAddressValue = R.assoc("full_address", R.prop("text", address), value);
        } catch (error) {
          console.error("Reverse geocode failed:", error);
        } finally {
          if (!isStale()) {
            onChange(addressField, fullAddressValue);
            setCoordinates({ lat: latitude, lng: longitude });
          }
        }
      }

      const isInCityBoundary = await checkCoordsInBoundary(latitude, longitude);
      if (!isStale()) {
        setIsInCityBoundary(isInCityBoundary);

        if (isInCityBoundary) {
          onSave(addressField);
        }
      }

      return fullAddressValue;
    },
    [
      addressField,
      blockWhileSaving,
      checkCoordsInBoundary,
      geocoder,
      onChange,
      onSave,
      setCoordinates,
      setIsInCityBoundary,
    ],
  );

  return handleLocationChange;
};

// Filter out zoning field ids already dispalyed on form if we're on a form
const getFilteredZoningFieldIdents = (answerContext: AnswerContextValue): string[] => {
  const record = R.prop("record", answerContext);
  const zoningFieldIDs = getZoningFieldIDs(record);

  if (!R.pathEq("RequirementSection", ["context", "type"], answerContext)) {
    return zoningFieldIDs;
  }
  const requirement = R.path(["context", "requirement"], answerContext);
  if (!requirement) return zoningFieldIDs;

  const requirementFieldIDs = getProjectRequirementFieldIDs(record, requirement);
  return R.without(requirementFieldIDs, zoningFieldIDs);
};

export default useZoningField;
